import {
  createContext,
  useState,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";
import { Patient } from "../ModalReservation/ModalReservation";
import { CalendarInfo } from "../Calendar";

export type DatiTessera = {
  ablAPCARD: string;
  titoloAPCARD: string;
};

export type TimeLapse = {
  firstTimeStamp: number | null;
  secondTimeStamp: number | null;
};

interface DataContextType {
  data: any;
  setData: Dispatch<SetStateAction<any>>;
  setAppointmentsList: Dispatch<SetStateAction<any[]>>;
  numeroAppuntamento: string;
  numeroAppuntamentoRiepilogo: string;
  setNumeroAppuntamento: (value: string) => void;
  setNumeroAppuntamentoRiepilogo: (value: string) => void;
  pazienteData: Patient | undefined;
  setPazienteData: (value: Patient | undefined) => void;
  // NOTA: pazienteDataSingolo è stato aggiunto a seguito di richieste addizionali avvenute in seguito
  // per accedere al dettaglio di un paziente, il pazienteData è alla base di controlli per gestire
  // il flusso di input e di operazioni a seguito dello state della pagina. Inserire un nuovo state, seppur
  // duplicato ha semplicato l'implementazione della richiesta emersa in seguito senza dover intaccare
  // il flusso di "patientData"
  pazienteDataSingolo: Patient | undefined;
  setPazienteDataSingolo: (value: Patient | undefined) => void;
  statusAvanzamentoPrenotazione: number;
  setStatusAvanzamentoPrenotazione: (value: number) => void;
  idsGenerati: IDs | undefined;
  setIdsGenerati: (value: IDs | undefined) => void;
  addAppointmentToList: (appt: any) => void;
  clearAppointmentsList: () => void;
  appointmentsList: any[]; // oppure tipizza meglio se hai un tipo preciso
  tipoAgendaLock: string;
  setTipoAgendaLock: (value: string) => void;
  ente: string;
  setEnte: (value: string) => void;
  medico: string;
  setMedico: (value: string) => void;
  showOrarioGiornaliero: boolean;
  setShowOrarioGiornaliero: (value: boolean) => void;
  dataCalendarSelezionato: CalendarInfo | undefined;
  setDataCalendarSelezionato: (value: CalendarInfo | undefined) => void;
  combinedProcedurePayload: CombinedProcedure | undefined;
  setCombinedProcedurePayload: (value: CombinedProcedure | undefined) => void;
  groupableProceduresPayload: Record<string, string> | null;
  setGroupableProceduresPayload: (value: Record<string, string> | null) => void;
  idPacada: string;
  setIdPacada: (value: string) => void;
  idPacate: string;
  setIdPacate: (value: string) => void;
  aziendaPrenotazione: string;
  setAziendaPrenotazione: (value: string) => void;
  // dettaglio della carta associata
  apCardDettaglio: DatiTessera;
  setApCardDettaglio: (value: DatiTessera) => void;
  // Coda per i modali RegionData
  regionDataQueue: string[];
  setRegionDataQueue: Dispatch<SetStateAction<string[]>>;
  enqueueRegionData: (ids: string[]) => void;
  dequeueRegionData: () => string | undefined;
  // Hints per pre-popolare il form del modale (regionDatadetails)
  regionDataHints: Record<string, RegionDataHint>;
  setRegionDataHints: Dispatch<SetStateAction<Record<string, RegionDataHint>>>;
  advanceRegionDataQueue: () => { next?: string; remainingCount: number };
  timestampAnagrafica: TimeLapse;
  setTimestampAnagrafica: (value: TimeLapse) => void;
  timeExpiry: number | null;
  setTimeExpiry: (value : number | null) => void;
}

interface RegionDataHint {
  specialita?: string;
  prestazione?: string;
  codSiss?: string;
}

interface IDs {
  IdPacata: string;
  IdPacapr: string;
  IdPacada: string;
  idPacadaOld?: string;
}

export type GestioneOrariGiornalieriProps = {
  crud: string;
  aziendaPrenotazione: string;
  CODMEDINT?: string;
  CODPGMEDT?: string;
  CODTPGPRE?: string;
  dataIni?: string;
  dataFin?: string;
  DATARIF?: string;
  B1ORADA?: string;
  B1ORAA?: string;
  B2ORADA?: string;
  B2ORAA?: string;
  B3ORADA?: string;
  B3ORAA?: string;
  B4ORADA?: string;
  B4ORAA?: string;
  B5ORADA?: string;
  B5ORAA?: string;
  IDPAMEDG?: string;
};

export type CombinedProcedure = {
  aziendaPrenotazione: string;
  idPacada: string;
  idMedpgm: string;
  codIstcom: string;
};

const DataContext = createContext<DataContextType | undefined>(undefined);

const ContextWrapper = ({ children }: { children: ReactNode }) => {
  const [dataTransfer, setDataTransfer] = useState([] as any);
  const [numeroAppuntamento, setNumeroAppuntamento] = useState<string>("");
  const [numeroAppuntamentoRiepilogo, setNumeroAppuntamentoRiepilogo] =
    useState<string>("");
  const [pazienteData, setPazienteData] = useState<Patient>();
  const [pazienteDataSingolo, setPazienteDataSingolo] = useState<
    Patient | undefined
  >();
  const [idsGenerati, setIdsGenerati] = useState<IDs>();
  const [appointmentsList, setAppointmentsList] = useState<any[]>([]);
  const [tipoAgendaLock, setTipoAgendaLock] = useState<string>("");
  const [ente, setEnte] = useState<string>("");
  const [medico, setMedico] = useState<string>("");
  const [showOrarioGiornaliero, setShowOrarioGiornaliero] =
    useState<boolean>(false);
  //Questo è l'idPacada che recuperiamo dalla prenotaSlot. L'ho salvato qui perché serve per i dati regionali
  const [idPacada, setIdPacada] = useState<string>("");
  //Recupero l'idPacate, che serve per la stampa dei moduli
  const [idPacate, setIdPacate] = useState<string>("");
  const [aziendaPrenotazione, setAziendaPrenotazione] = useState<string>("");
  //State del dettaglio della carta
  const [apCardDettaglio, setApCardDettaglio] = useState<DatiTessera>({
    ablAPCARD: "0",
    titoloAPCARD: "Gestione Tessera",
  });

  //timestamp per controllare dall'uscita all'ingresso della pagina
  const [timestampAnagrafica, setTimestampAnagrafica] = useState<TimeLapse>({
    firstTimeStamp: null,
    secondTimeStamp: null,
  });
  //timeLeftSaved
  const [timeExpiry, setTimeExpiry] = useState<number | null>(null);

  const [dataCalendarSelezionato, setDataCalendarSelezionato] = useState<
    CalendarInfo | undefined
  >();

  const [combinedProcedurePayload, setCombinedProcedurePayload] = useState<
    CombinedProcedure | undefined
  >();
  const [groupableProceduresPayload, setGroupableProceduresPayload] =
    useState<Record<string, string> | null>(null);

  const addAppointmentToList = (appointment: any) => {
    setAppointmentsList((prev) => [...prev, appointment]);
  };

  const clearAppointmentsList = () => {
    setAppointmentsList([]);
  };

  //Stato che contiene la coda di ID per aprire i modali RegionData
  const [regionDataQueue, setRegionDataQueue] = useState<string[]>([]);
  //Stato che contiene eventuali suggerimenti/precompilazioni per i campi del modale RegionData
  const [regionDataHints, setRegionDataHints] = useState<
    Record<string, RegionDataHint>
  >({});

  //Aggiunge nuovi ID in coda per i modali RegionData
  const enqueueRegionData = (ids: string[]) => {
    setRegionDataQueue((prev) => {
      // aggiungo in fondo gli ID nuovi, filtrando quelli vuoti
      const next = [...prev, ...ids.filter(Boolean)];
      // console.log("[enqueueRegionData] ids in arrivo:", ids);
      // console.log("[enqueueRegionData] stato precedente:", prev);
      // console.log("[enqueueRegionData] nuovo stato:", next);
      return next;
    });
  };

  // Estrae e rimuove il primo ID dalla coda (testa della lista)
  const dequeueRegionData = () => {
    let removed: string | undefined;
    setRegionDataQueue((prev) => {
      // prendo la testa (head) e tengo la coda (tail)
      const [head, ...tail] = prev;
      removed = head;
      console.log("[dequeueRegionData] head rimosso:", head);
      console.log("[dequeueRegionData] nuovo stato:", tail);
      return tail;
    });
    // restituisco l’ID rimosso
    return removed;
  };

  // Avanza nella coda rimuovendo il primo ID e prepara il prossimo
  const advanceRegionDataQueue = (): {
    next?: string;
    remainingCount: number;
  } => {
    let next: string | undefined;
    let remainingCount = 0;

    setRegionDataQueue((prev) => {
      if (prev.length === 0) return prev;

      // rimuovo la testa e aggiorno il prossimo elemento
      const [, ...tail] = prev;
      // prossimo ID da processare
      next = tail[0];
      // quanti ID restano in coda
      remainingCount = tail.length;

      // console.log(
      //   "[advanceRegionDataQueue] rimossa testa; next:",
      //   next,
      //   "remainingCount:",
      //   remainingCount
      // );
      return tail;
    });

    // ritorno il prossimo ID e quanti restano
    return { next, remainingCount };
  };

  /* Use state per gestire le logiche di stato di compilazione dell'appuntamento da prenotare
   *  Status 0: stato default per eseguire il form di una prima prenotazione singola
   *  Status 1: stato per gestione di compilazione di un ciclo di prenotazioni, con stesso numero prenotazione
   *  Status 2: stato per gestione di compilazione di una nuova prestazione, con stesso numero prenotazione
   *  Status 3: stato per gestione di compilazione di una prestazione in corso di una modifica per data */
  const [statusAvanzamentoPrenotazione, setStatusAvanzamentoPrenotazione] =
    useState<number>(0);

  const prop = {
    data: dataTransfer,
    setData: setDataTransfer,
    numeroAppuntamento,
    setNumeroAppuntamento,
    pazienteData,
    setPazienteData,
    pazienteDataSingolo,
    setPazienteDataSingolo,
    statusAvanzamentoPrenotazione,
    setStatusAvanzamentoPrenotazione,
    idsGenerati,
    setIdsGenerati,
    tipoAgendaLock,
    setTipoAgendaLock,
    numeroAppuntamentoRiepilogo,
    setNumeroAppuntamentoRiepilogo,
    appointmentsList,
    setAppointmentsList,
    addAppointmentToList,
    clearAppointmentsList,
    ente,
    setEnte,
    medico,
    setMedico,
    showOrarioGiornaliero,
    setShowOrarioGiornaliero,
    dataCalendarSelezionato,
    setDataCalendarSelezionato,
    combinedProcedurePayload,
    setCombinedProcedurePayload,
    groupableProceduresPayload,
    setGroupableProceduresPayload,
    idPacada,
    setIdPacada,
    idPacate,
    setIdPacate,
    aziendaPrenotazione,
    setAziendaPrenotazione,
    regionDataQueue,
    setRegionDataQueue,
    enqueueRegionData,
    dequeueRegionData,
    regionDataHints,
    setRegionDataHints,
    advanceRegionDataQueue,
    apCardDettaglio,
    setApCardDettaglio,
    timestampAnagrafica,
    setTimestampAnagrafica,
    timeExpiry,
    setTimeExpiry
  };

  return <DataContext.Provider value={prop}>{children}</DataContext.Provider>;
};

export { ContextWrapper, DataContext };
